// CoeDashboardPage.tsx
import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    useTheme,
    alpha,
    CircularProgress,
    Chip,
    Stack,
    Divider,
    Button,
    Paper 
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import GavelIcon from '@mui/icons-material/Gavel';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'; 

// Assuming these exist in your project
import Header from '../components/Header';
import Footer from '../components/Footer';
import EnquirySection from '../components/EnquirySection';

// --- Types ---
interface CoeRecord {
    recordID: number;
    month: string;
    biddingNo: string;
    vehicleClass: string;
    quota: string;
    bidsSuccess: string;
    bidsReceived: string;
    premium: string;
}

interface ProcessedCategory {
    id: string;
    title: string;
    desc: string;
    currentPrice: number;
    prevPrice: number;
    quota: string;
    bids: string;
    historyData: number[];
    pqp: number | string;
}

interface CoeDashboardPageProps {
    mode: 'light' | 'dark';
    setMode: (mode: 'light' | 'dark') => void;
}

// --- HARDCODED DATA ---

// PQP Data for 2026-04 based on the provided image
const hardcodedPQPData: Record<string, number | string> = {
    'Category A': 107407,
    'Category B': 114366,
    'Category C': 75751,
    'Category D': 8670,
    'Category E': 'N.A'
};

// COE History Data (Jan 2026 - Mar 2026)
const hardcodedCOEData: CoeRecord[] = [
  // 2026-03
  { recordID: 1, month: '2026-03', biddingNo: '2', vehicleClass: 'Category A', premium: '111890', quota: '1264', bidsReceived: '1895', bidsSuccess: '1264' },
  { recordID: 2, month: '2026-03', biddingNo: '2', vehicleClass: 'Category B', premium: '115568', quota: '812', bidsReceived: '1185', bidsSuccess: '812' },
  { recordID: 3, month: '2026-03', biddingNo: '2', vehicleClass: 'Category C', premium: '78000', quota: '290', bidsReceived: '438', bidsSuccess: '290' },
  { recordID: 4, month: '2026-03', biddingNo: '2', vehicleClass: 'Category D', premium: '9589', quota: '546', bidsReceived: '726', bidsSuccess: '546' },
  { recordID: 5, month: '2026-03', biddingNo: '2', vehicleClass: 'Category E', premium: '118119', quota: '246', bidsReceived: '422', bidsSuccess: '246' },
  { recordID: 6, month: '2026-03', biddingNo: '1', vehicleClass: 'Category A', premium: '108220', quota: '1268', bidsReceived: '1762', bidsSuccess: '1268' },
  { recordID: 7, month: '2026-03', biddingNo: '1', vehicleClass: 'Category B', premium: '114002', quota: '815', bidsReceived: '1261', bidsSuccess: '815' },
  { recordID: 8, month: '2026-03', biddingNo: '1', vehicleClass: 'Category C', premium: '76000', quota: '290', bidsReceived: '488', bidsSuccess: '290' },
  { recordID: 9, month: '2026-03', biddingNo: '1', vehicleClass: 'Category D', premium: '8602', quota: '545', bidsReceived: '643', bidsSuccess: '545' },
  { recordID: 10, month: '2026-03', biddingNo: '1', vehicleClass: 'Category E', premium: '114890', quota: '245', bidsReceived: '514', bidsSuccess: '245' },

  // 2026-02
  { recordID: 11, month: '2026-02', biddingNo: '2', vehicleClass: 'Category A', premium: '106501', quota: '1289', bidsReceived: '1975', bidsSuccess: '1289' },
  { recordID: 12, month: '2026-02', biddingNo: '2', vehicleClass: 'Category B', premium: '105001', quota: '813', bidsReceived: '1353', bidsSuccess: '813' },
  { recordID: 13, month: '2026-02', biddingNo: '2', vehicleClass: 'Category C', premium: '74999', quota: '294', bidsReceived: '502', bidsSuccess: '294' },
  { recordID: 14, month: '2026-02', biddingNo: '2', vehicleClass: 'Category D', premium: '7989', quota: '548', bidsReceived: '665', bidsSuccess: '548' },
  { recordID: 15, month: '2026-02', biddingNo: '2', vehicleClass: 'Category E', premium: '112890', quota: '239', bidsReceived: '571', bidsSuccess: '239' },
  { recordID: 16, month: '2026-02', biddingNo: '1', vehicleClass: 'Category A', premium: '106320', quota: '1265', bidsReceived: '1982', bidsSuccess: '1265' },
  { recordID: 17, month: '2026-02', biddingNo: '1', vehicleClass: 'Category B', premium: '110890', quota: '813', bidsReceived: '966', bidsSuccess: '813' },
  { recordID: 18, month: '2026-02', biddingNo: '1', vehicleClass: 'Category C', premium: '74801', quota: '292', bidsReceived: '420', bidsSuccess: '292' },
  { recordID: 19, month: '2026-02', biddingNo: '1', vehicleClass: 'Category D', premium: '8289', quota: '550', bidsReceived: '645', bidsSuccess: '550' },
  { recordID: 20, month: '2026-02', biddingNo: '1', vehicleClass: 'Category E', premium: '116000', quota: '271', bidsReceived: '462', bidsSuccess: '271' },

  // 2026-01
  { recordID: 21, month: '2026-01', biddingNo: '2', vehicleClass: 'Category A', premium: '109501', quota: '1301', bidsReceived: '2303', bidsSuccess: '1301' },
  { recordID: 22, month: '2026-01', biddingNo: '2', vehicleClass: 'Category B', premium: '121634', quota: '803', bidsReceived: '1207', bidsSuccess: '803' },
  { recordID: 23, month: '2026-01', biddingNo: '2', vehicleClass: 'Category C', premium: '75202', quota: '283', bidsReceived: '434', bidsSuccess: '283' },
  { recordID: 24, month: '2026-01', biddingNo: '2', vehicleClass: 'Category D', premium: '8860', quota: '538', bidsReceived: '609', bidsSuccess: '538' },
  { recordID: 25, month: '2026-01', biddingNo: '2', vehicleClass: 'Category E', premium: '120891', quota: '299', bidsReceived: '426', bidsSuccess: '299' },
  { recordID: 26, month: '2026-01', biddingNo: '1', vehicleClass: 'Category A', premium: '102009', quota: '1285', bidsReceived: '1658', bidsSuccess: '1285' },
  { recordID: 27, month: '2026-01', biddingNo: '1', vehicleClass: 'Category B', premium: '119100', quota: '824', bidsReceived: '1128', bidsSuccess: '824' },
  { recordID: 28, month: '2026-01', biddingNo: '1', vehicleClass: 'Category C', premium: '75503', quota: '287', bidsReceived: '394', bidsSuccess: '287' },
  { recordID: 29, month: '2026-01', biddingNo: '1', vehicleClass: 'Category D', premium: '8689', quota: '544', bidsReceived: '639', bidsSuccess: '544' },
  { recordID: 30, month: '2026-01', biddingNo: '1', vehicleClass: 'Category E', premium: '122000', quota: '278', bidsReceived: '528', bidsSuccess: '278' },
];


// --- Helper: Sparkline ---
const Sparkline = ({ data, color }: { data: number[]; color: string }) => {
    if (!data || data.length < 2) return null;

    const height = 60;
    const width = 250;
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;

    const points = data
        .map((val, index) => {
            const x = (index / (data.length - 1)) * width;
            const y = height - ((val - min) / range) * (height - 10) - 5;
            return `${x},${y}`;
        })
        .join(' ');

    return (
        <Box sx={{ width: '100%', height: height, overflow: 'hidden', mt: 2 }}>
            <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
                <defs>
                    <linearGradient id={`grad-${color}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={color} stopOpacity={0.2} />
                        <stop offset="100%" stopColor={color} stopOpacity={0} />
                    </linearGradient>
                </defs>
                <polygon fill={`url(#grad-${color})`} points={`0,${height} ${points} ${width},${height}`} />
                <polyline fill="none" stroke={color} strokeWidth="3" points={points} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        </Box>
    );
};

// --- Component: CoeCard ---
const CoeCard = ({ data }: { data: ProcessedCategory }) => {
    const theme = useTheme();
    const difference = data.currentPrice - data.prevPrice;
    const isUp = difference > 0;
    const trendColor = isUp ? theme.palette.error.main : theme.palette.success.main;
    const trendBg = isUp ? alpha(theme.palette.error.main, 0.1) : alpha(theme.palette.success.main, 0.1);
    const TrendIcon = isUp ? TrendingUpIcon : TrendingDownIcon;

    const fmt = (val: number | string) => {
        if (typeof val === 'string') return val;
        return new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD', maximumFractionDigits: 0 }).format(val);
    };

    return (
        <Card
            elevation={3}
            sx={{
                height: '100%',
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: theme.shadows[10],
                }
            }}
        >
            <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h6" fontWeight="800" sx={{ lineHeight: 1 }}>{data.title}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', mt: 0.5 }}>{data.desc}</Typography>
                </Box>
            </Box>

            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', pt: 3 }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="600" gutterBottom>CURRENT PREMIUM</Typography>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Typography variant="h5" fontWeight="900" color="text.primary">{fmt(data.currentPrice)}</Typography>
                        <Chip
                            icon={<TrendIcon style={{ color: trendColor }} />}
                            label={fmt(Math.abs(difference))}
                            sx={{
                                bgcolor: trendBg,
                                color: trendColor,
                                fontWeight: 'bold',
                                borderRadius: 1,
                                height: 'auto',
                                py: 0.5,
                                px: 0.5,
                                flexShrink: 0,
                                '& .MuiChip-label': { paddingLeft: 1, paddingRight: 1 }
                            }}
                        />
                    </Stack>
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Box sx={{ mb: 'auto', mt: 2 }}>
                    <Grid container spacing={0}>
                        <Grid size={{ xs: 4 }}>
                            <Stack spacing={0.5} alignItems="center">
                                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ opacity: 0.7 }}>
                                    <DirectionsCarIcon sx={{ fontSize: 16 }} />
                                    <Typography variant="caption" fontWeight="bold">QUOTA</Typography>
                                </Stack>
                                <Typography variant="h6" fontWeight="700">{data.quota}</Typography>
                            </Stack>
                        </Grid>
                        <Grid size={{ xs: 4 }} sx={{ borderLeft: `1px solid ${theme.palette.divider}`, borderRight: `1px solid ${theme.palette.divider}` }}>
                            <Stack spacing={0.5} alignItems="center">
                                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ opacity: 0.7 }}>
                                    <GavelIcon sx={{ fontSize: 16 }} />
                                    <Typography variant="caption" fontWeight="bold">BIDS</Typography>
                                </Stack>
                                <Typography variant="h6" fontWeight="700">{data.bids}</Typography>
                            </Stack>
                        </Grid>
                        <Grid size={{ xs: 4 }}>
                            <Stack spacing={0.5} alignItems="center">
                                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ opacity: 0.7 }}>
                                    <MonetizationOnIcon sx={{ fontSize: 16 }} />
                                    <Typography variant="caption" fontWeight="bold">PQP</Typography>
                                </Stack>
                                <Typography variant="h6" fontWeight="700">
                                    {typeof data.pqp === 'number' ? `$${(data.pqp / 1000).toFixed(1)}k` : data.pqp}
                                </Typography>
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>

                <Box sx={{ mt: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary" fontWeight="600">3-MONTH TREND</Typography>
                        <Button size="small" sx={{ fontSize: 10, minWidth: 'auto', p: 0 }}>FULL HISTORY</Button>
                    </Stack>
                    <Sparkline data={data.historyData} color={theme.palette.primary.main} />
                </Box>
            </CardContent>
        </Card>
    );
};

// --- Main Page Component ---
const CoeDashboardPage = ({ mode, setMode }: CoeDashboardPageProps) => {
    const theme = useTheme();
    const [rawCoeList, setRawCoeList] = useState<CoeRecord[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        const fetchCOEData = () => {
            setLoading(true);
            
            // Simulate network request to allow the spinner to show momentarily
            setTimeout(() => {
                setRawCoeList(hardcodedCOEData);
                setLoading(false);
            }, 500);
        };
        fetchCOEData();
    }, []);

    // Extract Latest Bidding Info for the Banner
    const latestBiddingInfo = useMemo(() => {
        if (!rawCoeList || rawCoeList.length === 0) return null;
        // Sort specifically to get the absolute latest across all categories
        const sorted = [...rawCoeList].sort((a, b) => {
            if (a.month !== b.month) return b.month.localeCompare(a.month);
            return b.biddingNo.localeCompare(a.biddingNo);
        });
        return sorted[0]; // Returns the latest record found
    }, [rawCoeList]);

    const processedData = useMemo(() => {
        if (!rawCoeList || rawCoeList.length === 0) return {};

        const categories = [
            { id: 'Category A', title: 'CAT A', desc: 'Cars ≤ 1600cc & 130bhp' },
            { id: 'Category B', title: 'CAT B', desc: 'Cars > 1600cc or 130bhp' },
            { id: 'Category C', title: 'CAT C', desc: 'Goods Vehicles & Buses' },
            { id: 'Category E', title: 'CAT E', desc: 'Open Category' },
        ];

        const result: Record<string, ProcessedCategory> = {};

        categories.forEach(cat => {
            const records = rawCoeList.filter(r => r.vehicleClass === cat.id);
            records.sort((a, b) => {
                if (a.month !== b.month) return b.month.localeCompare(a.month);
                return b.biddingNo.localeCompare(a.biddingNo);
            });

            if (records.length > 0) {
                const current = records[0];
                const previous = records.length > 1 ? records[1] : current;
                const history = records.slice(0, 6).map(r => parseInt(r.premium)).reverse();
                
                // Retrieve explicitly hardcoded PQP from the 2026-04 map
                const mappedPQP = hardcodedPQPData[cat.id] || 'N.A';

                result[cat.id] = {
                    ...cat,
                    currentPrice: parseInt(current.premium),
                    prevPrice: parseInt(previous.premium),
                    quota: current.quota,
                    bids: current.bidsReceived,
                    historyData: history,
                    pqp: mappedPQP
                };
            }
        });
        return result;
    }, [rawCoeList]);

    // ---------------------------------------------
    // HELPER: Calculate Next Tender Date
    // ---------------------------------------------
    const getNextTenderDate = () => {
        if (!latestBiddingInfo?.month || !latestBiddingInfo?.biddingNo) return "Loading...";

        const currentMonthStr = latestBiddingInfo.month.split('-')[1]; // Extracts "03" from "2026-03"
        const isFirstBidding = latestBiddingInfo.biddingNo === '1';

        const months = [
            "January", "February", "March", "April", "May", "June",
            "July", "August", "September", "October", "November", "December"
        ];
        
        const currentIdx = parseInt(currentMonthStr, 10) - 1;
        const currentMonthName = months[currentIdx];

        if (isFirstBidding) {
            return `17 ${currentMonthName}`;
        } else {
            const nextIdx = (currentIdx + 1) % 12;
            const nextMonthName = months[nextIdx];
            return `1 ${nextMonthName}`;
        }
    };

    return (
        <>
            <Header />
            <Box sx={{ minHeight: '100vh', py: 8, mt: 8 }}>
                <Container maxWidth="lg">
                    <Box textAlign="center" mb={4}>
                        <Typography variant="overline" color="primary" fontWeight="bold" letterSpacing={1.5}>Market Watch</Typography>
                        <Typography variant="h3" fontWeight="800" gutterBottom>LATEST COE PRICES</Typography>
                        <Typography variant="body1" color="text.secondary" maxWidth="600px" mx="auto">Accurate data to help you make smarter buying and selling decisions.</Typography>
                    </Box>

                    {/* ---------------- INFO BANNER SECTION ---------------- */}
                    {!loading && latestBiddingInfo && (
                        <Paper
                            elevation={0}
                            sx={{
                                mb: 6,
                                p: { xs: 2, md: 3 },
                                borderRadius: 3,
                                border: `1px solid ${theme.palette.divider}`,
                                bgcolor: theme.palette.background.paper,
                                maxWidth: '800px',
                                mx: 'auto'
                            }}
                        >
                            <Grid container spacing={2} alignItems="center" justifyContent="center">
                                {/* Left: Current Status */}
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Stack direction="row" spacing={2} alignItems="center" justifyContent={{ xs: 'center', md: 'flex-start' }}>
                                        <Box>
                                            <Typography variant="caption" display="block" color="text.secondary" fontWeight="bold" letterSpacing={1}>
                                                CURRENT RESULTS
                                            </Typography>
                                            <Typography variant="subtitle1" fontWeight="800">
                                                {latestBiddingInfo.month} <span style={{ opacity: 0.4 }}>|</span> {latestBiddingInfo.biddingNo === '1' ? '1st' : '2nd'} Bidding
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Grid>

                                {/* Divider for desktop */}
                                <Grid size={{ xs: 0 }} sx={{ display: { xs: 'none', md: 'block' } }}>
                                    <Divider orientation="vertical" flexItem sx={{ height: '40px', mx: 2 }} />
                                </Grid>

                                {/* Right: Next Tender Date */}
                                <Grid size={{ xs: 12, md: 5 }}>
                                    <Stack direction="row" spacing={2} alignItems="center" justifyContent={{ xs: 'center', md: 'flex-end' }}>
                                        <Box sx={{ textAlign: { xs: 'center', md: 'right' } }}>
                                            <Typography variant="caption" display="block" color="text.secondary" fontWeight="bold" letterSpacing={1}>
                                                NEXT TENDER
                                            </Typography>
                                            <Typography variant="subtitle1" fontWeight="800" color="text.primary">
                                                {getNextTenderDate()}
                                            </Typography>
                                        </Box>
                                        <Box sx={{
                                            bgcolor: '#f3f4f6',
                                            color: '#64748b',
                                            p: 1.5,
                                            borderRadius: '50%',
                                            display: 'flex'
                                        }}>
                                            <CalendarMonthIcon />
                                        </Box>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Paper>
                    )}
                    {/* ----------------------------------------------------- */}

                    {loading && <Box display="flex" justifyContent="center" my={10}><CircularProgress /></Box>}

                    {!loading && (
                        <Grid container spacing={4}>
                            {processedData['Category A'] && <Grid size={{ xs: 12, md: 6, lg: 3 }}><CoeCard data={processedData['Category A']} /></Grid>}
                            {processedData['Category B'] && <Grid size={{ xs: 12, md: 6, lg: 3 }}><CoeCard data={processedData['Category B']} /></Grid>}
                            {processedData['Category C'] && <Grid size={{ xs: 12, md: 6, lg: 3 }}><CoeCard data={processedData['Category C']} /></Grid>}
                            {processedData['Category E'] && <Grid size={{ xs: 12, md: 6, lg: 3 }}><CoeCard data={processedData['Category E']} /></Grid>}
                        </Grid>
                    )}
                </Container>
            </Box>

            <EnquirySection />
            <Footer />
        </>
    );
}

export default CoeDashboardPage;